import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { saas, errMsg } from "@/lib/saasApi";

export default function AdminSettings() {
  const [pool, setPool] = useState(null);
  const [balance, setBalance] = useState("");
  const [note, setNote] = useState("");
  const [topup, setTopup] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => saas.adminGetPool().then(({ data }) => { setPool(data); setBalance(String(data.balance ?? 0)); setNote(data.note || ""); }).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const save = async () => {
    const b = parseInt(balance, 10);
    if (!Number.isInteger(b) || b < 0) return toast.error("Enter a valid amount (≥ 0).");
    setBusy(true);
    try { const { data } = await saas.adminSetPool(b, note); setPool(data); toast.success("Rendy balance reconciled."); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const doTopup = async () => {
    const a = parseInt(topup, 10);
    if (!Number.isInteger(a) || a === 0) return toast.error("Enter a whole number (not 0).");
    setBusy(true);
    try { const { data } = await saas.adminTopupPool(a, ""); setPool(data); setBalance(String(data.balance)); setTopup(""); toast.success("Updated."); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl space-y-6" data-testid="admin-settings">
      <h1 className="font-heading text-2xl font-bold text-re-charcoal">Settings</h1>

      <div className="rounded-2xl border border-re-line bg-white p-6" data-testid="pool-settings">
        <h2 className="font-heading text-lg font-semibold text-re-charcoal">Estimated Rendy balance</h2>
        <div className="mt-3 flex gap-2 rounded-xl bg-re-soft p-3 text-sm text-re-muted">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-re-red" />
          <p>This balance is maintained locally and is <strong>not directly synchronised</strong> with the official Rendy account. Rendy provides no balance endpoint. Update this after checking your Rendy dashboard.</p>
        </div>

        <p className="mt-4 text-sm text-re-muted">Current estimate: <span className="font-heading text-2xl font-bold text-re-charcoal" data-testid="pool-current">{pool?.balance ?? 0}</span></p>
        <p className="text-xs text-re-muted">Last reconciliation: {pool?.syncedAt ? new Date(pool.syncedAt).toLocaleString("en-GB") : "never"}</p>

        <div className="mt-5 space-y-3 border-t border-re-line pt-5">
          <div className="space-y-1.5"><Label>Set new estimated balance</Label><Input type="number" value={balance} onChange={(e) => setBalance(e.target.value)} data-testid="pool-balance" className="border-re-line" /></div>
          <div className="space-y-1.5"><Label>Note (optional)</Label><Textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="pool-note" className="border-re-line" /></div>
          <Button onClick={save} disabled={busy} data-testid="pool-save" className="rounded-full bg-re-red text-white hover:bg-re-deepred">{busy ? "Working…" : "Reconcile"}</Button>
        </div>

        <div className="mt-5 flex items-end gap-2 border-t border-re-line pt-5">
          <div className="flex-1 space-y-1.5"><Label>Quick adjust (+/-)</Label><Input type="number" value={topup} onChange={(e) => setTopup(e.target.value)} data-testid="pool-topup" className="border-re-line" /></div>
          <Button variant="outline" onClick={doTopup} disabled={busy} data-testid="pool-topup-submit" className="rounded-full">Apply</Button>
        </div>
      </div>

      <div className="rounded-2xl border border-re-line bg-white p-6" data-testid="billing-settings">
        <h2 className="font-heading text-lg font-semibold text-re-charcoal">Billing</h2>
        <p className="mt-1 text-sm text-re-muted">Customers are currently billed <strong>manually</strong>. Stripe integration is prepared but not active yet.</p>
      </div>
    </div>
  );
}
